import { Box, Flex, HStack, Heading, Tag, Text } from '@chakra-ui/react';
import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { SETTINGS_CONTENT_MAX_WIDTH, SETTINGS_TABLE_GUTTER } from './constants';

const SettingsPageActionsContext = createContext<HTMLDivElement | null>(null);

/**
 * Portals its children onto the enclosing SettingsPage's title row, right
 * aligned. Sections render this to place header-level buttons even though the
 * page shell (and its title row) is rendered above them by Settings.tsx.
 */
export const SettingsPageActions = ({ children }: { children: ReactNode }) => {
    const container = useContext(SettingsPageActionsContext);
    return container ? createPortal(children, container) : null;
};

export type SettingsPageVariant = 'form' | 'table';

export interface SettingsPageProps {
    title: string;
    description?: string;
    /**
     * Small label beside the title, for a section that is not required to be
     * configured ("Optional") or otherwise needs qualifying.
     */
    badge?: string;
    /**
     * `form` centers content at a readable width between the sidebar and the
     * right edge. `table` runs edge to edge so tables align with the dashboard
     * and RO manager lists.
     */
    variant?: SettingsPageVariant;
    /** Buttons rendered on the title row, right aligned. */
    actions?: ReactNode;
    children: ReactNode;
}

/**
 * Page shell for a single settings section. The settings content area supplies
 * no padding of its own, so each page owns its gutters; that is what lets the
 * table variant reach both edges without negative margins.
 */
const SettingsPage = ({
    title,
    description,
    badge,
    variant = 'form',
    actions,
    children,
}: SettingsPageProps) => {
    // Portal target for SettingsPageActions; a callback-ref state so the slot
    // re-renders portalled content once the node mounts.
    const [actionsEl, setActionsEl] = useState<HTMLDivElement | null>(null);

    const header = (
        <Flex justify="space-between" align="flex-start" gap={4} w="100%">
            <Box>
                <HStack spacing={2} align="center">
                    <Heading size="md" color="gray.800">
                        {title}
                    </Heading>
                    {badge && (
                        <Tag
                            size="sm"
                            borderRadius="md"
                            bg="gray.100"
                            color="gray.600"
                            fontWeight="medium"
                            textTransform="none"
                        >
                            {badge}
                        </Tag>
                    )}
                </HStack>
                {description && (
                    <Text fontSize="sm" color="gray.500" mt={1}>
                        {description}
                    </Text>
                )}
            </Box>
            <HStack spacing={2} flexShrink={0} ref={setActionsEl}>
                {actions}
            </HStack>
        </Flex>
    );

    if (variant === 'table') {
        // The table body owns its own fixed-height scroll with a sticky thead,
        // so the title row stays outside it and remains pinned. The content
        // region is a clipped flex column: the table's scroll box is flex-1 and
        // absorbs leftover height, so a trailing footer (pagination) always
        // sits at the bottom edge of the page.
        return (
            <SettingsPageActionsContext.Provider value={actionsEl}>
                <Flex direction="column" h="100%" minH={0} w="100%">
                    <Box px={SETTINGS_TABLE_GUTTER} pt={6} pb={4} flexShrink={0} w="100%">
                        {header}
                    </Box>
                    <Flex direction="column" flex={1} minH={0} w="100%" overflow="hidden">
                        {children}
                    </Flex>
                </Flex>
            </SettingsPageActionsContext.Provider>
        );
    }

    // The header lives inside the same padded, centered column as the cards,
    // so its left edge lines up with theirs and it scrolls away with the page.
    return (
        <SettingsPageActionsContext.Provider value={actionsEl}>
            <Box h="100%" overflowY="auto" px={8} pt={6} pb={8} w="100%">
                <Box maxW={SETTINGS_CONTENT_MAX_WIDTH} mx="auto">
                    <Box pb={6}>{header}</Box>
                    {children}
                </Box>
            </Box>
        </SettingsPageActionsContext.Provider>
    );
};

export default SettingsPage;
